/**
 * Modal — componente base premium.
 *
 * Renderizado via createPortal pra escapar de containers com overflow.
 * - Backdrop preto com blur
 * - Card branco rounded-2xl shadow-2xl
 * - Animação fade + scale (150ms)
 * - ESC fecha (dispara onCancel)
 * - Click no backdrop fecha (dispara onCancel)
 * - Trap de foco automático no botão primário
 */
"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";


export interface ModalProps {
  open:        boolean;
  onCancel:    () => void;
  title?:      string;
  width?:      number;             // default 440
  children:    React.ReactNode;
  footer?:     React.ReactNode;
  hideClose?:  boolean;
}


export function Modal({
  open, onCancel, title, width = 440, children, footer, hideClose,
}: ModalProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  // ESC fecha
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  // Foco automático no primeiro botão primário
  useEffect(() => {
    if (!open || !ref.current) return;
    const t = setTimeout(() => {
      const primary = ref.current?.querySelector<HTMLButtonElement>("[data-modal-primary]");
      primary?.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [open]);

  if (typeof window === "undefined" || !open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 animate-modal-fade"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Card */}
      <div
        ref={ref}
        className="relative bg-app-surface rounded-2xl shadow-2xl border border-app max-h-[90vh] overflow-hidden flex flex-col animate-modal-scale"
        style={{ width: `min(100%, ${width}px)` }}
      >
        {(title || !hideClose) && (
          <div className="flex items-start justify-between px-6 pt-6 pb-3">
            {title && (
              <h2 className="text-base font-semibold text-app-1 leading-tight pr-6">
                {title}
              </h2>
            )}
            {!hideClose && (
              <button
                onClick={onCancel}
                className="btn-icon -mr-1 -mt-1"
                aria-label={t("modal.close")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className="px-6 pb-1 overflow-y-auto">
          {children}
        </div>

        {footer && (
          <div className="px-6 py-4 flex items-center justify-end gap-2 border-t border-app-subtle mt-2">
            {footer}
          </div>
        )}
      </div>

      <style jsx global>{`
        @keyframes modal-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes modal-scale-in { from { transform: scale(.96); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        .animate-modal-fade  { animation: modal-fade-in 150ms cubic-bezier(.4,0,.2,1); }
        .animate-modal-scale { animation: modal-scale-in 150ms cubic-bezier(.4,0,.2,1); }
      `}</style>
    </div>,
    document.body,
  );
}


// ─── Botões padronizados pro footer ──────────────────────────────────────
export function ModalBtnCancel({ children, onClick }: { children?: React.ReactNode; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      className="btn-secondary btn-md"
    >
      {children || t("modal.cancel")}
    </button>
  );
}

export function ModalBtnPrimary({
  children, onClick, danger, disabled, loading,
}: {
  children: React.ReactNode;
  onClick:  () => void;
  danger?:  boolean;
  disabled?: boolean;
  loading?:  boolean;
}) {
  const cls = danger
    ? "btn-danger btn-md"
    : "btn-primary btn-md";
  return (
    <button
      data-modal-primary="true"
      onClick={onClick}
      disabled={disabled || loading}
      className={`flex items-center gap-2 ${cls}`}
    >
      {loading && (
        <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
      )}
      {children}
    </button>
  );
}
